/**
 * What a failed password request is allowed to tell the user (AL-1780, SEG-03).
 *
 * The screens used to render whatever came back from the server. That is how
 * the account list leaked: a request for an address with no account answered
 * "we could not find a user with this email address", so anyone could walk a
 * list of addresses and learn which ones belong to customers.
 *
 * The backend now answers identically either way. These rules are the second
 * half — they keep the frontend from reintroducing the difference, and they
 * hold even against an older backend that still discriminates.
 */

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

const bodyOf = (error: unknown): { message?: unknown; errors?: unknown } | undefined =>
  (error as { response?: { data?: { message?: unknown; errors?: unknown } } })?.response?.data;

/**
 * Whether the recovery screen may show a failure instead of the confirmation.
 *
 * Only for things the user can act on: waiting out a rate limit, or a request
 * that did not reach the server. Every other refusal takes the same path a real
 * account takes, because the difference is the disclosure.
 */
export const isActionableFailure = (error: unknown): boolean => {
  const status = statusOf(error);

  if (status === undefined) {
    // No response at all: the request never landed, so the confirmation panel
    // would be telling the user an email is on its way when it is not.
    return true;
  }

  return status === 429 || status >= 500;
};

export type ResetFailure =
  | { kind: 'throttled' }
  | { kind: 'failed' }
  | { kind: 'invalidLink' }
  | { kind: 'password'; messages: string[] };

/** Field errors the user genuinely needs, because the browser cannot check them. */
const PASSWORD_FIELDS = ['password', 'password_confirmation'];

const passwordMessages = (bag: unknown): string[] => {
  if (!bag || typeof bag !== 'object') return [];

  return Object.entries(bag as Record<string, unknown>)
    .filter(([field]) => PASSWORD_FIELDS.includes(field))
    .flatMap(([, value]) => (Array.isArray(value) ? value : [value]))
    .filter((entry): entry is string => typeof entry === 'string' && entry.trim() !== '');
};

/**
 * What the reset screen may show.
 *
 * The password rules the server enforces come through, because the user has to
 * know what to change — the browser cannot tell whether a password appears in a
 * breach corpus. Everything else collapses into one answer: the link does not
 * work. "No such user" and "bad token" are the same thing to someone holding a
 * broken link, and telling them apart is what names the account.
 */
export const resetFailureKind = (error: unknown): ResetFailure => {
  const status = statusOf(error);

  if (status === undefined) return { kind: 'failed' };
  if (status === 429) return { kind: 'throttled' };
  if (status >= 500) return { kind: 'failed' };

  const body = bodyOf(error);
  const messages = [...passwordMessages(body?.errors), ...passwordMessages(body?.message)];

  if (messages.length > 0) return { kind: 'password', messages };

  return { kind: 'invalidLink' };
};
